import { getWithAddedDays, isAfter } from '@noths/polaris-client-utils';
import { createSelector } from '@reduxjs/toolkit';

import { selectDecoratedDeliveryOptions } from 'src/redux/delivery/selectors';
import type { DecoratedDeliveryOption } from 'src/redux/delivery/types';
import { selectProductDeliveryByEvent } from 'src/redux/product/selectors';

type DeliveryOption = Pick<DecoratedDeliveryOption, 'exactDate' | 'date' | 'min' | 'max'>;

export const hasExactDeliveryDate = ({ date, exactDate }: DeliveryOption) =>
  exactDate && date != null;

export const isLateDelivery = (date: Date | string, constrainingDate: Date | string) =>
  isAfter(date, constrainingDate);

export const getMaxDeliveryDate = (max: number) => getWithAddedDays(new Date(), max);

export const selectCheapestDeliveryOptionPrice = createSelector(
  [selectDecoratedDeliveryOptions, selectProductDeliveryByEvent],
  (deliveryOptions: DecoratedDeliveryOption[] | [], lastDeliveryByEvent): number | null => {
    if (deliveryOptions.length === 0 || !lastDeliveryByEvent?.arriveByEvent) {
      return null;
    }

    const cheapestDeliveryOptionPrice = (deliveryOptions as DecoratedDeliveryOption[]).reduce(
      (cheapestDeliveryPrice: number | null, option: DecoratedDeliveryOption) => {
        const { lastDeliveryBy } = lastDeliveryByEvent;
        const {
          date,
          max,
          price: { amount },
        } = option;

        const numberAmount = Number(amount);
        const hasExactDeliveryDateOption = hasExactDeliveryDate(option);

        const isTimelyExactDeliveryDateOption =
          hasExactDeliveryDateOption && !isLateDelivery(date!, lastDeliveryBy);

        if (hasExactDeliveryDateOption && !isTimelyExactDeliveryDateOption) {
          return cheapestDeliveryPrice;
        }

        const isCheaperDeliveryOption =
          cheapestDeliveryPrice == null || numberAmount < cheapestDeliveryPrice;

        if (isTimelyExactDeliveryDateOption && isCheaperDeliveryOption) {
          return numberAmount;
        }

        const maxDeliveryDate = getMaxDeliveryDate(max!);

        if (isLateDelivery(maxDeliveryDate, lastDeliveryBy)) {
          return cheapestDeliveryPrice;
        }

        return isCheaperDeliveryOption ? numberAmount : cheapestDeliveryPrice;
      },
      null as null | number,
    );

    return cheapestDeliveryOptionPrice;
  },
);
